/**
 * Word Clash Game Types
 *
 * All TypeScript types and interfaces for the Word Clash (Scrabble-inspired) game.
 */

// ============================================================================
// Tile Types
// ============================================================================

export interface Tile {
  letter: string;
  value: number;
  isBlank: boolean;
  id: string; // unique identifier for drag/drop tracking
}

export enum PremiumSquareType {
  NONE = 'NONE',
  DOUBLE_LETTER = 'DL',
  TRIPLE_LETTER = 'TL',
  DOUBLE_WORD = 'DW',
  TRIPLE_WORD = 'TW',
  CENTER = 'CENTER'
}

export interface BoardCell {
  tile: Tile | null;
  premium: PremiumSquareType;
  row: number;
  col: number;
}

// ============================================================================
// Player Types
// ============================================================================

export interface PlayerRack {
  tiles: Tile[];
  playerId: string;
}

export interface WordGamePlayer {
  id: string;
  name: string;
  score: number;
  rack: Tile[];
  isAI: boolean;
  consecutivePasses: number;
}

// ============================================================================
// Move / Placement Types
// ============================================================================

export type Direction = 'horizontal' | 'vertical';

export interface TilePlacement {
  tile: Tile;
  row: number;
  col: number;
}

export interface WordPlacement {
  tiles: TilePlacement[];
  direction: Direction;
  startRow: number;
  startCol: number;
}

export interface PlacedWord {
  word: string;
  score: number;
  tiles: TilePlacement[];
  direction: Direction;
}

// ============================================================================
// Game State Types
// ============================================================================

export type WordGamePhase = 'waiting' | 'playing' | 'ended';

export interface WordGameState {
  board: BoardCell[][];
  players: WordGamePlayer[];
  currentPlayerIndex: number;
  tileBag: Tile[];
  phase: WordGamePhase;
  turnNumber: number;
  lastPlayedWord: PlacedWord | null;
  lastPlacedPositions: { row: number; col: number }[];
  moveHistory: MoveHistoryEntry[];
  winner: string | null;
  gameOverReason: string | null;
}

export interface MoveHistoryEntry {
  playerIndex: number;
  type: 'play' | 'exchange' | 'pass';
  words?: PlacedWord[];
  totalScore?: number;
  timestamp: number;
}

// ============================================================================
// AI Types
// ============================================================================

export type WordGameDifficulty = 'easy' | 'medium' | 'hard';

export interface AIMove {
  type: 'play' | 'exchange' | 'pass';
  placement?: WordPlacement;
  exchangeTiles?: Tile[];
}

// ============================================================================
// Game Settings
// ============================================================================

export interface WordGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  playerCount: number;
  playerNames: string[];
  aiDifficulty?: WordGameDifficulty;
  aiPlayerCount?: number;
}

// ============================================================================
// Statistics Types
// ============================================================================

export interface WordGameStatistics {
  singlePlayer: {
    easy: WordGameStats;
    medium: WordGameStats;
    hard: WordGameStats;
  };
  localMultiplayer: WordGameStats;
  onlineMultiplayer: WordGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface WordGameStats {
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  highestWordScore: number;
  highestGameScore: number;
  totalWordsPlayed: number;
  longestWord: string;
  averageScore: number;
}
